import { useEffect, useState } from "react";
import Accordion from "../DropDown/Accordion";
import AddAccountModal from "../Modal/AddAccountModal";
import AppService from "../../Services/appService/AppService.js";
import BaseButton from "../Buttons/BaseButton";
import Container from "../Helper/Container.jsx";
import Search from "../Search/Search.jsx";
import translate from "../../Utils/translate";
import useMediaQuery from "../../Hooks/useMediaQuery";
import usePrompt from "../../Hooks/usePrompt";
import ViewPasswordModal from "../Modal/ViewPasswordModal";

const ACCOUNTS = "accounts";
const ADD_ACCOUNT = "addAccount";
const UPDATE_ACCOUNT = "updateAccount";
const VIEW_PASSWORD = "viewPassword";

const Accounts = () => {
  const [refetch, setRefetch] = useState(false);

  const [activeAccount, setActiveAccount] = useState(null);
  const [query, setQuery] = useState("");
  // 'accounts', 'addAccount', 'viewPassword', updateAccount'
  const [state, setState] = useState(ACCOUNTS);
  const { isDesktop } = useMediaQuery();
  const prompt = usePrompt();
  const [errorId, setErrorId] = useState("");

  useEffect(() => {
    // if is not desktop, hide the modal
    isDesktop ? setState(ADD_ACCOUNT) : setState(ACCOUNTS);
  }, [isDesktop]);

  async function handleSubmitAdd(values) {
    const mappedValues = {
      ...values,
      tags: [values.tags],
    };

    try {
      await AppService.addApp(mappedValues);

      prompt.createSuccess(translate("success.app.add"));
      prompt.clearPrompt(errorId?.add);

      setRefetch(true);

      if (!isDesktop) {
        setState(ACCOUNTS);
      }
    } catch (error) {
      const errorId = prompt.createError(error);
      setErrorId((prevErrors) => ({ ...prevErrors, add: errorId }));
    }
  }

  async function handleViewPassword(event, activeAccount) {
    try {
      const account = await AppService.getAppPassword(activeAccount?.id);

      setActiveAccount(account);
      setState(VIEW_PASSWORD);
      prompt.clearPrompt(errorId?.password);
    } catch (error) {
      const errorId = prompt.createError(error);
      setErrorId((prevErrors) => ({ ...prevErrors, password: errorId }));
    }
  }

  async function updateAccount(values) {
    try {
      const body = {
        ...values,
        tags: [values?.tags],
      };

      const result = await AppService.updateApp(activeAccount?.accountId, body);
      setActiveAccount(result);
      setState(ACCOUNTS);
      prompt.createSuccess(translate("success.app.update"));
      prompt.clearPrompt(errorId?.updated);
      setRefetch(true);

      if (!isDesktop) {
        return setState(ACCOUNTS);
      }
      setState(ADD_ACCOUNT);
    } catch (error) {
      const errorId = prompt.createError(error);
      setErrorId((prevErrors) => ({ ...prevErrors, updated: errorId }));
    }
  }

  function handleCloseModal() {
    setActiveAccount({ ...activeAccount, password: null });

    if (isDesktop) {
      return setState(ADD_ACCOUNT);
    }

    setState(ACCOUNTS);
  }

  function getAccountState() {
    if (!state.includes(ADD_ACCOUNT) && !state.includes(VIEW_PASSWORD) && !state.includes(UPDATE_ACCOUNT)) {
      return null;
    }

    return (
      <Container>
        {state.includes(ADD_ACCOUNT) && (
          <AddAccountModal handleCloseModal={!isDesktop ? handleCloseModal : null} handleSubmit={handleSubmitAdd} />
        )}

        {state.includes(VIEW_PASSWORD) && (
          <ViewPasswordModal
            activeAccount={activeAccount}
            handleCloseModal={handleCloseModal}
            handleUpdate={() => setState(UPDATE_ACCOUNT)}
          />
        )}

        {state.includes(UPDATE_ACCOUNT) && (
          <AddAccountModal
            defaultValues={activeAccount}
            handleCloseModal={handleCloseModal}
            handleSubmit={updateAccount}
          />
        )}
      </Container>
    );
  }

  return (
    <section className="flex-grow flex flex-col overflow-y-auto lg:grid lg:grid-cols-2">
      <section className="flex-grow flex flex-col justify-between overflow-y-auto px-default pb-default sm:p-default">
        <h1 className="hidden text-xl font-bold mb-default sm:block">{translate("pages.account.title.password")}</h1>
        <Search query={query} setQuery={setQuery} />

        <div className="flex-grow overflow-y-auto">
          <Accordion handleViewPassword={handleViewPassword} query={query} refetch={refetch} />
        </div>

        <BaseButton
          disabled={state === ADD_ACCOUNT}
          onClick={() => setState(ADD_ACCOUNT)}
          text={translate("forms.buttons.add")}
        />
      </section>

      {getAccountState()}
    </section>
  );
};

export default Accounts;
